<script setup lang="ts">
  import { computed } from "vue";
  import { Download, Pencil, Trash } from "lucide-vue-next";
  import { ActionsDropdown, type ActionItem } from "@/components/episto-ui/actions-dropdown";
  import type { SurveyContextSource } from "@/types";
  import { formatFileSize } from "@utils/media";
  import { sourceFormat } from "./contextSources";

  const props = defineProps<{ source: SurveyContextSource; surveyToken: string }>();
  const emit = defineEmits<{ edit: [token: string]; remove: [token: string] }>();

  const format = computed(() => sourceFormat(props.source));
  const meta = computed(() =>
    [format.value.label, props.source.byte_size ? formatFileSize(props.source.byte_size) : null, props.source.user_name]
      .filter(Boolean)
      .join(" · "),
  );

  const actions = computed<ActionItem[]>(() => [
    {
      text: "Edit",
      icon: Pencil,
      condition: props.source.can_edit,
      function: (token: string) => emit("edit", token),
    },
    {
      text: "Download",
      icon: Download,
      condition: props.source.kind === "file",
      function: () => {
        window.location.href = `/api/surveys/${props.surveyToken}/context_sources/${props.source.token}/download`;
      },
    },
    {
      text: "Delete",
      icon: Trash,
      isDelete: true,
      msgConfirm: props.source.name,
      function: (token: string) => emit("remove", token),
    },
  ]);
</script>

<template>
  <div class="flex items-center gap-2.5 rounded-lg bg-muted p-2">
    <span
      class="flex size-8 shrink-0 items-center justify-center rounded-lg"
      :class="format.class">
      <component
        :is="format.icon"
        class="size-4"
        :stroke-width="1.8" />
    </span>
    <div class="flex min-w-0 flex-1 flex-col">
      <span class="truncate text-13 font-medium text-foreground">{{ source.name }}</span>
      <span class="truncate text-2xs text-muted-foreground">{{ meta }}</span>
      <span
        v-if="!source.supported"
        class="text-2xs leading-snug text-status-warning-strong">
        Format not supported by Episto AI
      </span>
      <span
        v-else-if="source.rejected"
        class="text-2xs leading-snug text-status-warning-strong">
        Episto AI could not read this file
      </span>
    </div>
    <ActionsDropdown
      :actions="actions"
      :token="source.token" />
  </div>
</template>
